import React, { useState, useEffect } from 'react';
import { MapPin, Phone, Clock, Instagram, ExternalLink, MessageCircle } from 'lucide-react';
import { businessData } from '../data/mock';

const Contact = () => {
  const [isOpen, setIsOpen] = useState(null);

  useEffect(() => {
    const checkIfOpen = () => {
      // Obtener hora actual en zona horaria de Argentina
      const argentinaTime = new Date().toLocaleString('en-US', {
        timeZone: 'America/Argentina/Buenos_Aires'
      });
      const now = new Date(argentinaTime);
      const day = now.getDay(); // 0 = Domingo, 1 = Lunes, etc.
      const hour = now.getHours();
      const minute = now.getMinutes();
      const currentTime = hour + minute / 60;

      // Domingo (0)
      if (day === 0) {
        // Domingo: 9:00-13:00
        return currentTime >= 9 && currentTime < 13;
      } else if (day >= 1 && day <= 6) {
        // Lunes a Sábado: 9:00-13:00 y 17:00-20:30
        return (currentTime >= 9 && currentTime < 13) || (currentTime >= 17 && currentTime < 20.5);
      }
      return false;
    };

    setIsOpen(checkIfOpen());
    
    // Actualizar cada minuto
    const interval = setInterval(() => {
      setIsOpen(checkIfOpen());
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  return (
    <section id="contacto" className="py-20 md:py-28 bg-[var(--color-cream)]">
      <div className="max-w-6xl mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-[var(--color-wine)] font-medium mb-2 tracking-wider uppercase text-sm">
            Visitános
          </span>
          <h2 className="text-3xl md:text-5xl font-serif text-[var(--color-black)] mb-4">
            Ubicación y Contacto
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Map and Address */}
          <div className="space-y-6">
            {/* Map Preview - Link directo sin iframe */}
            <a
              href={businessData.googleMapsLink}
              target="_blank"
              rel="noopener noreferrer"
              className="block rounded-xl overflow-hidden shadow-lg bg-[var(--color-wine)]/5 hover:shadow-xl transition-shadow group"
            >
              <div className="relative h-[300px] flex items-center justify-center bg-gradient-to-br from-[var(--color-wine)]/10 to-[var(--color-wine)]/5">
                <div className="text-center p-6">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[var(--color-wine)] flex items-center justify-center group-hover:scale-110 transition-transform">
                    <MapPin className="text-[var(--color-cream)]" size={40} />
                  </div>
                  <p className="text-[var(--color-black)] font-semibold text-lg mb-2">
                    Ver ubicación en Google Maps
                  </p>
                  <p className="text-[var(--color-gray)] text-sm">
                    Av. Facundo Zuviría 5131, Santa Fe
                  </p>
                  <span className="inline-flex items-center gap-2 mt-4 text-[var(--color-wine)] font-medium group-hover:text-[var(--color-wine-dark)]">
                    Abrir mapa
                    <ExternalLink size={16} />
                  </span>
                </div>
              </div>
            </a>

            {/* Address Card */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[var(--color-wine)]/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="text-[var(--color-wine)]" size={24} />
                </div>
                <div>
                  <h3 className="font-semibold text-[var(--color-black)] mb-1">Dirección</h3>
                  <p className="text-[var(--color-gray)]">{businessData.address}</p>
                  <a
                    href={businessData.googleMapsLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[var(--color-wine)] font-medium mt-2 hover:text-[var(--color-wine-dark)] transition-colors"
                  >
                    Cómo llegar
                    <ExternalLink size={14} />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Schedule and Contact */}
          <div className="space-y-6">
            {/* Open Status - Solo mostrar si se pudo determinar */}
            {isOpen !== null && (
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${
                isOpen 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-red-100 text-red-700'
              }`}>
                <span className={`w-2 h-2 rounded-full animate-pulse ${
                  isOpen ? 'bg-green-500' : 'bg-red-500'
                }`} />
                <span className="font-medium">
                  {isOpen ? 'Abierto ahora' : 'Cerrado'}
                </span>
              </div>
            )}

            {/* Schedule Card */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[var(--color-wine)]/10 flex items-center justify-center">
                  <Clock className="text-[var(--color-wine)]" size={24} />
                </div>
                <h3 className="font-semibold text-[var(--color-black)] text-lg">Horarios de atención</h3>
              </div>

              <div className="space-y-3">
                {businessData.schedule.map((item, index) => (
                  <div
                    key={item.day}
                    className={`flex justify-between items-center py-2 ${
                      index < businessData.schedule.length - 1
                        ? 'border-b border-[var(--color-cream-dark)]'
                        : ''
                    }`}
                  >
                    <span className="font-medium text-[var(--color-black)]">
                      {item.day}
                    </span>
                    <span className="text-[var(--color-gray)] text-sm md:text-base">
                      {item.hours}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Contact Buttons */}
            <div className="space-y-4">
              <a
                href={businessData.whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-3 bg-[var(--color-wine)] text-[var(--color-cream)] px-6 py-4 rounded-xl font-semibold text-lg transition-all hover:bg-[var(--color-wine-dark)] hover:shadow-lg w-full"
              >
                <MessageCircle size={24} />
                Contactar por WhatsApp
              </a>

              <a
                href={`tel:${businessData.phone}`}
                className="flex items-center justify-center gap-3 bg-white border-2 border-[var(--color-wine)] text-[var(--color-wine)] px-6 py-4 rounded-xl font-semibold text-lg transition-all hover:bg-[var(--color-wine)] hover:text-[var(--color-cream)] w-full"
              >
                <Phone size={24} />
                {businessData.phone}
              </a>

              <a
                href={businessData.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-3 bg-[var(--color-cream-dark)] text-[var(--color-black)] px-6 py-4 rounded-xl font-medium transition-all hover:bg-[var(--color-gold)] hover:text-[var(--color-black)] w-full"
              >
                <Instagram size={24} />
                @indiana.vinoteca
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
